import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Fragment, useMemo, memo, useEffect, useLayoutEffect, useReducer, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, Button } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { LANGUAGES } from "../../../lib/helpers";
import { getExtraField, createExtraField, updateExtraField, getTabs } from "../../../lib/services/fields";
import { formatDynamicFields, formatDependenceFields, normalizeFieldName, getTabsOptions, isEntityList, DUPLICATES_SECTION_SCHEMA, DUPLICATES_SECTION_SCHEMA_DISABLED, ENABLED_DUPLICATE_ENTITIES } from "../lib";
import { getFieldTypesList } from "../../../lib/literals";
import { getFieldsValidations, getFieldValidation } from "../../../lib/services/fieldsValidations";
import { getParameter } from "../../../lib/services/parameters";
import { showErrorToast } from "../../../lib/errors";
import { CRUD_TYPE_ADD, CRUD_TYPE_EDIT, CRUD_TYPE_DUPLICATE } from "../../../lib/constants";
import ForceForm, { validate, flatList } from "../../../components/ForceForm";
import HyperDrawer from "../../../components/HyperDrawer";
import Tour from "../../../components/Tour";
import FormValueList from "../../ValueLists/FormValueList";
import FormFieldsValidations from "../../FieldsValidations/FormFieldsValidations";
import FormTab from "../FormTab";
import { getDescription } from "../../../utils/custom";
import { executeSandbox } from "../../../utils/sandbox";
import { searchValues } from "../../../lib/services/valueLists";
import { successToast } from "../../../utils/toast";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'FormTab');
const FIELD_TYPE = {
  multivalue: 'checkboxGroup',
  list: 'valueList',
  text: 'text',
  decimal: 'decimals',
  int: 'integer',
  currency: 'currency',
  datetime: 'dateTime',
  bit: 'switch',
  percentage: 'percentage'
};
const FORM_SCHEMA = {
  context: {
    fields: {
      branches: {
        table: 'tblSucursales'
      },
      dynamicFieldValues: {
        table: null
      },
      relevantFields: {
        table: 'tblTiposUsuario'
      }
    }
  },
  required: ['fieldName', 'entity', 'type', 'description', 'valueListRelated'],
  properties: {
    fieldName: {
      maxLength: 200,
      type: ['string']
    },
    entity: {
      maxLength: 200,
      type: ['string']
    },
    type: {
      maxLength: 200,
      type: ['string']
    },
    description: {
      maxLength: 200,
      type: ['string']
    },
    branches: {
      type: ['array'],
      items: {
        type: 'integer'
      }
    },
    dynamicFieldValues: {
      type: ['array'],
      items: {
        type: 'integer'
      }
    },
    customValidation: {
      type: ['integer']
    },
    defaultValue: {
      maxLength: 200,
      type: ['string']
    },
    length: {
      maxLength: 200,
      type: ['integer']
    },
    valueListRelated: {
      type: ['string']
    },
    tabId: {
      type: ['integer']
    },
    dynamicField: {
      type: ['string']
    },
    dependenceField: {
      type: ['string']
    },
    visible: {
      type: ['boolean']
    },
    mobileVisibility: {
      type: ['boolean']
    },
    visibleInList: {
      type: ['boolean']
    },
    searchable: {
      type: ['boolean']
    },
    mandatory: {
      type: ['boolean']
    },
    readOnly: {
      type: ['boolean']
    },
    fuzzySearch: {
      type: ['boolean']
    },
    visibleInPhoneCallForm: {
      type: ['boolean']
    },
    restApi: {
      type: ['boolean']
    },
    mandatoryRestApi: {
      type: ['boolean']
    }
  },
  layout: {
    schemaId: 'createExtraField',
    uiGrid: 'linearVertical',
    composition: [{
      schemaId: 'entity',
      label: 'cfm_label_entity',
      uiType: 'list',
      placeholder: 'label_selectone'
    }, {
      schemaId: 'tabId',
      label: 'cfm_label_tab',
      uiType: 'list',
      placeholder: 'label_selectone'
    }, {
      schemaId: 'description',
      label: 'cfm_label_field_name',
      uiType: 'expandableComponent',
      labelForExpand: ['cfm_label_view_more_lang', 'cfm_label_view_less_lang'],
      composition: [{
        schemaId: 'description',
        uiType: 'text',
        label: 'cfm_label_field_name',
        placeholder: 'cfm_label_spanish'
      }, ...LANGUAGES.map(language => ({
        label: `cfm_label_${language.label}`,
        schemaId: `description${language.key}`,
        uiType: 'text',
        placeholder: 'cfm_placeholder_type_value'
      }))]
    }, {
      schemaId: 'fieldName',
      label: 'cfm_label_fieldname_api_name',
      uiType: 'text',
      placeholder: 'cfm_placeholder_type_value'
    }, {
      schemaId: 'type',
      label: 'cfm_label_data_type',
      uiType: 'list',
      placeholder: 'label_selectone'
    }, {
      schemaId: 'isEditable',
      label: 'cfm_label_editable_by_user',
      uiType: 'checkbox'
    }, {
      schemaId: 'includeTime',
      label: 'cfm_label_includes_time',
      uiType: 'checkbox',
      isHidden: true
    }, {
      schemaId: 'valueListRelated',
      label: 'cfm_label_related_table',
      uiType: 'list',
      placeholder: 'label_selectone',
      composition: [{
        schemaId: 'buttonvalue',
        uiType: 'button',
        label: 'cfm_label_description'
      }],
      isHidden: true
    }, {
      schemaId: 'customValidation',
      label: 'cfm_label_validation',
      uiType: 'list',
      placeholder: 'label_selectone',
      noOptionsPlaceholder: 'cfm_helptext_no_validations'
    }, {
      schemaId: 'defaultValue',
      label: 'cfm_label_default_value',
      uiType: 'text',
      placeholder: 'cfm_placeholder_type_value',
      isHidden: true
    }, {
      schemaId: 'length',
      label: 'cfm_label_max_characters',
      uiType: 'number',
      placeholder: 'cfm_placeholder_integer',
      isHidden: true
    }, {
      schemaId: 'branches',
      label: 'cfm_label_visibility_environments',
      uiType: 'multiList',
      placeholder: 'label_selectone',
      emptyPlaceholder: 'cfm_label_all'
    }, {
      schemaId: 'dynamicField',
      label: 'cfm_label_parent_dynamic_name',
      uiType: 'list',
      placeholder: 'label_selectone',
      emptyPlaceholder: 'cfm_placeholder_type_value'
    }, {
      schemaId: 'dynamicFieldValues',
      label: 'cfm_label_parent_dynamic_value',
      uiType: 'multiList',
      isFuzzy: true,
      placeholder: 'label_selectone',
      emptyPlaceholder: 'cfm_label_all'
    }, {
      schemaId: 'dependenceField',
      label: 'cfm_label_parent_dependency',
      uiType: 'list',
      placeholder: 'label_selectone'
    }, {
      schemaId: 'dependentAlert',
      uiType: 'message',
      placeholder: 'cfm_label_dependant_field_extra_info',
      isHidden: true
    }, {
      schemaId: 'relevantFields',
      label: 'cfm_label_featured_by_usertype',
      uiType: 'multiList',
      placeholder: 'label_selectone'
    }, {
      schemaId: 'generalSettings',
      uiType: 'checkboxGroup',
      uiGrid: 'linearVertical',
      label: 'cfm_label_general_config',
      composition: [{
        schemaId: 'visible',
        label: 'cfm_label_visible_all_devices',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'mobileVisibility',
        label: 'cfm_label_visible_mobility',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'visibleInList',
        label: 'cfm_label_include_in_views',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'searchable',
        label: 'cfm_label_include_search',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'mandatory',
        label: 'cfm_label_mandatory_only_apps',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'readOnly',
        label: 'cfm_label_readonly',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'fuzzySearch',
        label: 'cfm_label_is_fuzzy',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'visibleInPhoneCallForm',
        label: 'cfm_label_visible_in_phone_call_form',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'restApi',
        label: 'cfm_label_visible_restapi_v4',
        uiType: 'checkbox',
        helpTip: null
      }, {
        schemaId: 'mandatoryRestApi',
        label: 'cfm_label_mandatory_in_api',
        uiType: 'checkbox',
        helpTip: null,
        isHidden: false
      }]
    }, {
      schemaId: 'comment',
      label: 'cfm_label_internal_comment',
      uiType: 'textarea',
      placeholder: 'cfm_label_internal_comment_desc'
    }]
  }
};
const initialState = {
  data: {},
  originalData: {},
  entities: [],
  crudType: null,
  errors: {},
  isOpen: false,
  isLoading: true,
  isSaving: false,
  valueLists: {},
  tables: {},
  stackIndex: 1,
  validationsCode: {},
  rowIndex: null
};
const loadedValues = [];
const notAllowEntities = ['coferta', 'tblcampaignsstatuses', 'tblcampaignstypes', 'tblsales', 'tblsurveysvalues', 'tblsalesorderslines', 'tblformstemplate', 'tblsalesorders'];
const entitiesLiterals = {
  Empresas: 'cfm_label_accounts',
  Contactos: 'cfm_label_contacts',
  Expedientes: 'cfm_label_opportunities',
  Gestiones: 'cfm_label_activities',
  tblAgenda: 'cfm_label_event_task',
  tblSalesOrders: 'cfm_label_salesorders',
  Productos: 'cfm_label_products',
  tblUsuarios: 'cfm_label_users'
};
const allowedForCustomValidations = ['Expedientes', 'Empresas', 'Contactos', 'tblAgenda'];
const allowedTypesOfValidation = ['text', 'int', 'percentage', 'decimal', 'currency', 'datetime'];
const allowedForRelevantFields = ['Empresas', 'Expedientes'];
const forbiddenForDynamicFields = ['tblSalesOrdersLines'];
function reducer(state, action) {
  var _action$data, _data, _data$dynamicField, _action$data2, _action$data3, _action$data3$dynamic, _state$data, _state$data$dynamicFi;
  switch (action.type) {
    case 'init':
      let crudType = CRUD_TYPE_ADD;
      let data = {};
      if (action.duplicate) {
        crudType = CRUD_TYPE_DUPLICATE;
      } else if ((_action$data = action.data) !== null && _action$data !== void 0 && _action$data.id) {
        crudType = CRUD_TYPE_EDIT;
      }
      if (crudType === CRUD_TYPE_ADD) {
        data = {
          entity: {
            label: action.entityLabel || action.entity,
            value: action.entity
          },
          length: '255',
          tabId: action.valueLists.tabs.find(el => el.value === -1),
          visible: true,
          mobileVisibility: true,
          visibleInList: true,
          searchable: true,
          mandatory: false,
          readOnly: false,
          fuzzySearch: false,
          visibleInPhoneCallForm: false,
          restApi: true,
          isEditable: true
        };
      } else {
        var _action$data$type, _action$valueLists$va, _action$data$relevant, _action$valueLists$ty, _action$valueLists$ta, _action$data$branches, _action$valueLists$dy, _action$data$dynamicF, _action$valueLists$de;
        const boolType = action.data.defaultValue === '1';
        const finalDefaultValue = (action.data.type === 'list' || ((_action$data$type = action.data.type) === null || _action$data$type === void 0 ? void 0 : _action$data$type.toLowerCase()) === 'multivalue') && action.data.defaultValue ? Number(action.data.defaultValue) : action.data.defaultValue;
        const valueListRelated = ((_action$valueLists$va = action.valueLists.valueListRelated) === null || _action$valueLists$va === void 0 ? void 0 : _action$valueLists$va.find(el => el.value === action.data.valueListRelated)) || {
          label: action.data.valueListRelated,
          value: action.data.valueListRelated
        };
        data = _objectSpread(_objectSpread({}, action.data), {}, {
          id: crudType === CRUD_TYPE_EDIT ? action.data.id : -1,
          fieldName: crudType === CRUD_TYPE_EDIT ? action.data.fieldName : action.data.fieldName.replace('Z_', '') + '_Copy',
          entity: {
            label: action.entity,
            value: action.entity
          },
          relevantFields: (_action$data$relevant = action.data.relevantFields) === null || _action$data$relevant === void 0 ? void 0 : _action$data$relevant.map(el => ({
            value: el.id,
            label: el.value
          })),
          type: (_action$valueLists$ty = action.valueLists.type) === null || _action$valueLists$ty === void 0 ? void 0 : _action$valueLists$ty.find(el => el.value === action.data.type),
          defaultValue: action.data.type === 'bit' ? boolType : finalDefaultValue,
          valueListRelated: action.valueLists.valueListRelated && action.data.valueListRelated ? valueListRelated : null,
          tabId: (_action$valueLists$ta = action.valueLists.tabs) === null || _action$valueLists$ta === void 0 ? void 0 : _action$valueLists$ta.find(el => el.value === action.data.tabId),
          branches: (_action$data$branches = action.data.branches) === null || _action$data$branches === void 0 ? void 0 : _action$data$branches.map(el => ({
            value: el.id,
            label: el.value
          })),
          dynamicField: (_action$valueLists$dy = action.valueLists.dynamicField) === null || _action$valueLists$dy === void 0 ? void 0 : _action$valueLists$dy.find(el => el.value === action.data.dynamicField),
          dynamicFieldValues: (_action$data$dynamicF = action.data.dynamicFieldValues) === null || _action$data$dynamicF === void 0 ? void 0 : _action$data$dynamicF.map(el => ({
            value: el.id,
            label: el.value
          })),
          dependenceField: (_action$valueLists$de = action.valueLists.dependenceField) === null || _action$valueLists$de === void 0 ? void 0 : _action$valueLists$de.find(el => el.value === action.data.dependenceField)
        });
      }
      const obj = _objectSpread(_objectSpread({}, state), {}, {
        originalData: data,
        data,
        fields: action.fields,
        entities: action.entities,
        tabs: action.tabs,
        crudType,
        errors: {},
        isOpen: true,
        isLoading: false,
        valueLists: action.valueLists,
        tables: _objectSpread(_objectSpread({}, state.tables), {}, {
          dynamicFieldValues: (_data = data) === null || _data === void 0 ? void 0 : (_data$dynamicField = _data.dynamicField) === null || _data$dynamicField === void 0 ? void 0 : _data$dynamicField.valueListRelated
        }),
        rowIndex: action.rowIndex || null
      });
      return obj;
    case 'close':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: false,
        isOpen: false
      });
    case 'changeEntity':
      return _objectSpread(_objectSpread({}, state), {}, {
        data: _objectSpread(_objectSpread(_objectSpread({}, state.data), action.data), {}, {
          tabId: null,
          dependenceField: null,
          dynamicField: null,
          dynamicFieldValues: null
        }),
        valueLists: _objectSpread(_objectSpread({}, state.valueLists), action.valueLists)
      });
    case 'changeTabs':
      return _objectSpread(_objectSpread({}, state), {}, {
        valueLists: _objectSpread(_objectSpread({}, state.valueLists), {}, {
          tabs: action.tabs
        })
      });
    case 'changeData':
      return _objectSpread(_objectSpread({}, state), {}, {
        data: _objectSpread(_objectSpread(_objectSpread({}, state.data), action.data), {}, {
          mandatoryRestApi: !action.data.restApi ? false : action.data.mandatoryRestApi
        })
      });
    case 'changeDataAndTables':
      const finalData = _objectSpread(_objectSpread(_objectSpread({}, state.data), action.data), {}, {
        dynamicFieldValues: (_action$data2 = action.data) !== null && _action$data2 !== void 0 && _action$data2.dynamicField && ((_action$data3 = action.data) === null || _action$data3 === void 0 ? void 0 : (_action$data3$dynamic = _action$data3.dynamicField) === null || _action$data3$dynamic === void 0 ? void 0 : _action$data3$dynamic.valueListRelated) === ((_state$data = state.data) === null || _state$data === void 0 ? void 0 : (_state$data$dynamicFi = _state$data.dynamicField) === null || _state$data$dynamicFi === void 0 ? void 0 : _state$data$dynamicFi.valueListRelated) ? action.data.dynamicFieldValues : undefined
      });
      const finalObj = _objectSpread(_objectSpread({}, state), {}, {
        data: finalData,
        tables: _objectSpread(_objectSpread({}, state.tables), {}, {
          dynamicFieldValues: action.tablesDynamicFieldValues
        })
      });
      return finalObj;
    case 'changeValueList':
      let label = action.data.name;
      if (action.isWeb) {
        var _action$data$name, _action$data$name2;
        if ((_action$data$name = action.data.name) !== null && _action$data$name !== void 0 && _action$data$name.match(/^tbl/)) {
          label = action.data.name.replace(/^tbl/, '');
        } else if ((_action$data$name2 = action.data.name) !== null && _action$data$name2 !== void 0 && _action$data$name2.match(/^Z_tbl/)) {
          label = action.data.name.replace(/^Z_tbl/, '');
        }
      } else {
        var _action$data$name3, _action$data$name4;
        if ((_action$data$name3 = action.data.name) !== null && _action$data$name3 !== void 0 && _action$data$name3.match(/^tbl/)) {
          label = `Z_${action.data.name} (${action.data.name.replace(/^tbl/, '')})`;
        } else if ((_action$data$name4 = action.data.name) !== null && _action$data$name4 !== void 0 && _action$data$name4.match(/^Z_tbl/)) {
          label = `${action.data.name} (${action.data.name.replace(/^Z_tbl/, '')})`;
        }
      }
      loadedValues.push({
        label,
        value: action.data.name,
        isEditable: action.data.isEditable,
        id: action.data.id || action.data.idList
      });
      state = _objectSpread(_objectSpread({}, state), {}, {
        data: _objectSpread(_objectSpread({}, state.data), {}, {
          valueListRelated: {
            label,
            value: action.data.name,
            isEditable: action.data.isEditable,
            id: action.data.id || action.data.idList
          }
        }),
        loadedValues
      });
      return state;
    case 'changeValidation':
      return _objectSpread(_objectSpread({}, state), {}, {
        data: _objectSpread(_objectSpread({}, state.data), {}, {
          customValidation: action.data
        })
      });
    case 'setErrors':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: false,
        errors: action.errors
      });
    case 'setSaving':
      return _objectSpread(_objectSpread({}, state), {}, {
        isSaving: action.value
      });
    case 'setValidationCode':
      return _objectSpread(_objectSpread({}, state), {}, {
        validationsCode: _objectSpread(_objectSpread({}, state.validationsCode), {}, {
          [action.validation]: action.code
        })
      });
    default:
      throw new Error('No action provided');
  }
}
const FormExtraField = _ref => {
  var _permissions$valueLis3, _permissions$valueLis4, _permissions$fieldsVa3, _permissions$fieldsVa4, _permissions$extraFie2, _state$data29, _state$data30, _state$data30$entity, _state$data33, _state$data33$entity, _state$data34, _state$data34$type, _overrides$formTabFie;
  let {
    onRef,
    refreshList,
    fieldsData,
    idSetupRole,
    setActiveRowId,
    processDataBeforeSave,
    entityLogName,
    entityLogSubmodule,
    avoidTours,
    overrides,
    onOpenUpgradeTabsInfo,
    onOpenUpgradeAdminInfo,
    entity,
    getEntityData
  } = _ref;
  const {
    t,
    language,
    permissions,
    logEvent,
    deviceCache,
    setDeviceCache,
    overridePermissions,
    customGeneralTab,
    overrideTours,
    customOnCloseTour,
    fieldsToHide = [],
    fieldsToAvoidExpand = [],
    hideCreateValidation,
    tracking,
    hideSaveAndCreateAnother,
    isWeb,
    canCreateTabs,
    onClickGoToAdmin
  } = useWeb5Context({
    overrides
  });
  const dumbUser = idSetupRole ? [8, 9].includes(idSetupRole) : false;
  const [state, dispatch] = useReducer(reducer, initialState);
  const schema = useRef(JSON.parse(JSON.stringify(FORM_SCHEMA)));
  const formValueLists = useRef();
  const formValidations = useRef();
  const formTab = useRef();
  const [stackIndex, setStackIndex] = useState(1);
  const classes = useStyles();

  // Child method
  useEffect(() => {
    onRef && onRef({
      open(_ref2) {
        let {
          id,
          entities,
          entity,
          duplicate,
          rowIndex
        } = _ref2;
        const fields = fieldsData[entity].fields;
        const getExtraFieldData = id => {
          if (id) {
            return getExtraField(id);
          }
          return Promise.resolve({
            data: null
          });
        };
        Promise.all([getParameter('implementation', 'CheckDuplicateFields'), getExtraFieldData(id)]).then(_ref3 => {
          var _parameter$data, _parameter$data2, _data$entity;
          let [parameter, {
            data
          }] = _ref3;
          // Checking duplicate param is disabled
          const duplicateParamDisabled = (parameter === null || parameter === void 0 ? void 0 : (_parameter$data = parameter.data) === null || _parameter$data === void 0 ? void 0 : _parameter$data.value) === '0' || !(parameter !== null && parameter !== void 0 && (_parameter$data2 = parameter.data) !== null && _parameter$data2 !== void 0 && _parameter$data2.value);
          const relatedOptions = entities === null || entities === void 0 ? void 0 : entities.filter(f => {
            var _f$name;
            return notAllowEntities.indexOf((_f$name = f.name) === null || _f$name === void 0 ? void 0 : _f$name.toLowerCase()) === -1;
          }).map(efEntity => {
            let {
              name: value
            } = efEntity;
            const customLiteral = t(entitiesLiterals[efEntity.name]) || null;
            value = value === 'Contactos' ? 'viewContactosFullName' : value;
            value = value === 'tblUsuarios' ? 'viewUsuariosFullName' : value;
            return {
              label: getDescription({
                tab: efEntity,
                dumbUser,
                customLiteral,
                isWeb
              }),
              value,
              id: efEntity === null || efEntity === void 0 ? void 0 : efEntity.id,
              isEditable: efEntity === null || efEntity === void 0 ? void 0 : efEntity.isEditable
            };
          });
          const valueLists = {
            entity: entities === null || entities === void 0 ? void 0 : entities.map(option => {
              const customLiteral = t(entitiesLiterals[option.name]) || null;
              return {
                label: getDescription({
                  tab: option,
                  dumbUser,
                  customLiteral,
                  isWeb
                }),
                value: option.name
              };
            }),
            type: getFieldTypesList(t),
            valueListRelated: relatedOptions,
            tabs: getTabsOptions(fields === null || fields === void 0 ? void 0 : fields.tab, t, customGeneralTab),
            dynamicField: formatDynamicFields((data === null || data === void 0 ? void 0 : data.fieldName) || '', (data === null || data === void 0 ? void 0 : (_data$entity = data.entity) === null || _data$entity === void 0 ? void 0 : _data$entity.value) || entity, fields, language, t),
            dynamicFieldValues: null,
            dependenceField: formatDependenceFields((data === null || data === void 0 ? void 0 : data.fieldName) || '', fields, language, t)
          };
          const layoutComposition = schema.current.layout.composition.filter(item => !['generalSettings', 'checkDuplicatesSection', 'comment'].includes(item.schemaId));
          const generalSettings = schema.current.layout.composition.find(item => item.schemaId === 'generalSettings');
          const internalComment = schema.current.layout.composition.find(item => item.schemaId === 'comment');
          const generalSettingsComposition = [...generalSettings.composition];
          const mandatoryIndex = generalSettingsComposition.findIndex(item => item.id === 'mandatory');
          const readOnlyIndex = generalSettingsComposition.findIndex(item => item.id === 'readOnly');
          const visibleInPhoneCallFormIndex = _objectSpread({}, generalSettingsComposition.find(item => item.id === 'visibleInPhoneCallForm'));
          const mandatoryRestApiIndex = generalSettingsComposition.find(item => item.id === 'mandatoryRestApi');
          generalSettingsComposition[mandatoryIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[mandatoryIndex]), {}, {
            isDisabled: data && !data.visible && !data.mobileVisibility
          });
          generalSettingsComposition[readOnlyIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[readOnlyIndex]), {}, {
            isDisabled: data && !data.visible && !data.mobileVisibility
          });
          generalSettingsComposition[visibleInPhoneCallFormIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[visibleInPhoneCallFormIndex]), {}, {
            isDisabled: data && !data.visible && !data.mobileVisibility
          });
          generalSettingsComposition[mandatoryRestApiIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[mandatoryRestApiIndex]), {}, {
            isHidden: !(data !== null && data !== void 0 && data.restApi)
          });
          const newSchema = _objectSpread(_objectSpread({}, schema.current), {}, {
            layout: _objectSpread(_objectSpread({}, schema.current.layout), {}, {
              composition: [...layoutComposition, _objectSpread(_objectSpread({}, generalSettings), {}, {
                composition: generalSettingsComposition
              }), _objectSpread(_objectSpread({}, internalComment), {}, {
                isHidden: overridePermissions
              })]
            })
          });

          // Duplicates param funcionality
          newSchema.layout.composition.push(_objectSpread({}, duplicateParamDisabled ? DUPLICATES_SECTION_SCHEMA_DISABLED : DUPLICATES_SECTION_SCHEMA));
          if (fieldsToAvoidExpand !== null && fieldsToAvoidExpand !== void 0 && fieldsToAvoidExpand.length) {
            var _newSchema$layout$com;
            (_newSchema$layout$com = newSchema.layout.composition) === null || _newSchema$layout$com === void 0 ? void 0 : _newSchema$layout$com.forEach(field => {
              if (fieldsToAvoidExpand.includes(field.schemaId)) {
                field.uiType = 'text';
              }
              return field;
            });
          }
          schema.current = newSchema;
          const finalData = _objectSpread(_objectSpread({}, data), {}, {
            customValidation: data !== null && data !== void 0 && data.customValidation ? {
              value: data.customValidation.id,
              label: data.customValidation.value
            } : null
          });
          dispatch({
            type: 'init',
            data: finalData,
            entities,
            fields,
            entity,
            entityLabel: t(entitiesLiterals[entity]) || entity,
            duplicate,
            valueLists,
            rowIndex
          });
        }).catch(error => {
          console.error(error);
          showErrorToast(t, error);
          dispatch({
            type: 'setErrors',
            errors: error
          });
        });
      }
    });
  }, [fieldsData, language, onRef, state.data, state.entities, state.fields, t, dumbUser, customGeneralTab, overridePermissions, fieldsToHide, fieldsToAvoidExpand, isWeb]);
  const validateDefaultValue = useCallback(_ref4 => {
    var _cleanData$customVali;
    let {
      isValid,
      errors,
      cleanData
    } = _ref4;
    const validation = ((_cleanData$customVali = cleanData.customValidation) === null || _cleanData$customVali === void 0 ? void 0 : _cleanData$customVali.value) || null;
    const getValidationCode = () => {
      if (!validation) return Promise.resolve({
        code: null
      });
      if (state.validationsCode[validation]) return Promise.resolve({
        code: state.validationsCode[validation]
      });
      return getFieldValidation(validation);
    };
    return getValidationCode().then(_ref5 => {
      let {
        code
      } = _ref5;
      if (!code) return null;
      if (validation && !state.validationsCode[validation]) dispatch({
        type: 'setValidationCode',
        validation,
        code
      });
      return executeSandbox(cleanData.defaultValue, code);
    }).then(error => ({
      isValid: isValid && !(error !== null && error !== void 0 && error.length),
      errors: _objectSpread(_objectSpread({}, errors), {}, {
        defaultValue: error !== null && error !== void 0 && error.length ? {
          message: error
        } : errors.defaultValue
      }),
      cleanData
    }));
  }, [state.validationsCode]);
  const onSave = useCallback(() => {
    var _state$data2, _schema$current, _schema$current2, _state$data3, _state$data3$relevant, _state$data4, _state$data4$userstyp;
    const required = (_state$data2 = state.data) !== null && _state$data2 !== void 0 && _state$data2.dynamicField ? [...((_schema$current = schema.current) === null || _schema$current === void 0 ? void 0 : _schema$current.required), 'dynamicFieldValues'] : (_schema$current2 = schema.current) === null || _schema$current2 === void 0 ? void 0 : _schema$current2.required;
    const finalForm = _objectSpread(_objectSpread({}, schema.current), {}, {
      required
    });
    const finalData = _objectSpread(_objectSpread({}, state.data), {}, {
      relevantFields: (_state$data3 = state.data) !== null && _state$data3 !== void 0 && (_state$data3$relevant = _state$data3.relevantFields) !== null && _state$data3$relevant !== void 0 && _state$data3$relevant.length ? state.data.relevantFields.map(el => el.value) : null,
      userstype: (_state$data4 = state.data) !== null && _state$data4 !== void 0 && (_state$data4$userstyp = _state$data4.userstype) !== null && _state$data4$userstyp !== void 0 && _state$data4$userstyp.length ? state.data.userstype.map(el => el.id) : null
    });
    validate(finalData, finalForm).then(validateDefaultValue).then(_ref6 => {
      let {
        isValid,
        errors,
        cleanData
      } = _ref6;
      if (!isValid) {
        dispatch({
          type: 'setErrors',
          errors
        });
      } else {
        dispatch({
          type: 'setSaving',
          value: true
        });
        // Tracking 'Link validation'
        if (!state.originalData.customValidation && cleanData.customValidation && (entityLogSubmodule === 'accountsTab' || entityLogSubmodule === 'contactsTab' || entityLogSubmodule === 'opportunitiesTab' || entityLogSubmodule === 'eventsAndTasksTab')) logEvent({
          event: entityLogName,
          submodule: entityLogSubmodule,
          functionality: 'linkValidation'
        });

        // Tracking 'Save'
        logEvent({
          event: entityLogName,
          submodule: entityLogSubmodule,
          functionality: state.crudType === CRUD_TYPE_EDIT ? 'update' : 'create'
        });
        let finalCleanData = flatList(cleanData, schema.current);
        let boolType = finalCleanData.defaultValue ? '1' : '0';
        let finalDefaultValue = finalCleanData.type === 'bit' ? boolType : `${finalCleanData.defaultValue}`;
        finalCleanData = _objectSpread(_objectSpread({}, finalCleanData), {}, {
          defaultValue: finalCleanData.defaultValue ? finalDefaultValue : ''
        });
        if (processDataBeforeSave) {
          finalCleanData = processDataBeforeSave(finalCleanData);
        }
        if (state.crudType === CRUD_TYPE_EDIT) {
          updateExtraField(state.data.id, finalCleanData).then(() => {
            refreshList && refreshList();
            setActiveRowId && setActiveRowId(null);
            dispatch({
              type: 'close'
            });
            successToast({
              t,
              text: t(state.data.isNew ? 'cfm_success_create_field' : 'cfm_success_update_field')
            });
          }).catch(error => {
            var _state$data5;
            console.error(error);
            showErrorToast(t, error, null, state === null || state === void 0 ? void 0 : (_state$data5 = state.data) === null || _state$data5 === void 0 ? void 0 : _state$data5.entity.value);
            dispatch({
              type: 'setErrors',
              errors: error
            });
          });
        } else {
          createExtraField(finalCleanData).then(response => {
            refreshList && refreshList({
              response,
              rowIndex: state.rowIndex
            });
            setActiveRowId && setActiveRowId(null);
            dispatch({
              type: 'close'
            });
            successToast({
              t,
              text: t(state.data.isNew ? 'cfm_success_create_field' : 'cfm_success_update_field')
            });
          }).catch(error => {
            var _state$data6;
            console.error(error);
            showErrorToast(t, error, null, state === null || state === void 0 ? void 0 : (_state$data6 = state.data) === null || _state$data6 === void 0 ? void 0 : _state$data6.entity.value);
            dispatch({
              type: 'setErrors',
              errors: error
            });
          });
        }
      }
    });
  }, [state.data, state.originalData.customValidation, state.crudType, validateDefaultValue, entityLogSubmodule, logEvent, entityLogName, processDataBeforeSave, refreshList, setActiveRowId, t, state.rowIndex]);
  const onSaveCreate = useCallback(() => {
    var _state$data7, _schema$current3, _schema$current4;
    const required = (_state$data7 = state.data) !== null && _state$data7 !== void 0 && _state$data7.dynamicField ? [...((_schema$current3 = schema.current) === null || _schema$current3 === void 0 ? void 0 : _schema$current3.required), 'dynamicFieldValues'] : (_schema$current4 = schema.current) === null || _schema$current4 === void 0 ? void 0 : _schema$current4.required;
    const finalForm = _objectSpread(_objectSpread({}, schema.current), {}, {
      required
    });
    validate(state === null || state === void 0 ? void 0 : state.data, finalForm).then(validateDefaultValue).then(_ref7 => {
      let {
        isValid,
        errors,
        cleanData
      } = _ref7;
      if (!isValid) {
        dispatch({
          type: 'setErrors',
          errors
        });
      } else {
        dispatch({
          type: 'setSaving',
          value: true
        });
        let finalCleanData = flatList(cleanData, schema.current);
        let boolType = finalCleanData.defaultValue ? '1' : '0';
        let finalDefaultValue = finalCleanData.type === 'bit' ? boolType : `${finalCleanData.defaultValue}`;
        finalCleanData = _objectSpread(_objectSpread({}, finalCleanData), {}, {
          defaultValue: finalCleanData.defaultValue ? finalDefaultValue : null
        });
        createExtraField(finalCleanData).then(() => {
          refreshList && refreshList();
          setActiveRowId && setActiveRowId(null);
          dispatch({
            type: 'setSaving',
            value: false
          });
          dispatch({
            type: 'init',
            data: {},
            entities: state.entities,
            fields: state.fields,
            entity: state.originalData.entity.value,
            entityLabel: state.originalData.entity.label,
            valueLists: state.valueLists
          });
          successToast({
            t,
            text: t(state.data.isNew ? 'cfm_success_create_field' : 'cfm_success_update_field')
          });
        }).catch(error => {
          var _state$data8;
          console.error(error);
          showErrorToast(t, error, null, state === null || state === void 0 ? void 0 : (_state$data8 = state.data) === null || _state$data8 === void 0 ? void 0 : _state$data8.entity.value);
          dispatch({
            type: 'setErrors',
            errors: error
          });
        });
      }
    });
  }, [state.data, state.entities, state.fields, state.originalData.entity, state.valueLists, validateDefaultValue, refreshList, setActiveRowId, t]);
  const locales = useMemo(() => {
    let title;
    switch (state.crudType) {
      case CRUD_TYPE_ADD:
        title = t('cfm_title_create_extra_field');
        break;
      case CRUD_TYPE_EDIT:
        title = t('cfm_title_edit_extra_field');
        break;
      case CRUD_TYPE_DUPLICATE:
        title = t('cfm_title_duplicate_extra_field');
        break;
      default:
        break;
    }
    return {
      title,
      cancel: t('cfm_action_cancel'),
      save: t('cfm_action_save'),
      saveAndCreate: t('cfm_action_add_not_close')
    };
  }, [state.crudType, t]);
  const onClickButtonList = useCallback(value => {
    setStackIndex(stackIndex + 1);
    formValueLists.current.open({
      id: value.id,
      isEditable: value.isEditable
    });
    (tracking === null || tracking === void 0 ? void 0 : tracking.updateValueList) && tracking.updateValueList();
  }, [stackIndex, tracking]);
  const valueListRelatedActions = useMemo(() => [{
    label: t('cfm_label_create_table'),
    onClick: () => {
      validate(state.data, _objectSpread(_objectSpread({}, schema.current), {}, {
        required: ['description']
      })).then(_ref8 => {
        let {
          isValid,
          errors
        } = _ref8;
        if (!isValid) {
          dispatch({
            type: 'setErrors',
            errors
          });
          return;
        }
        setStackIndex(stackIndex + 1);
        formValueLists.current.open({});
        (tracking === null || tracking === void 0 ? void 0 : tracking.createValueList) && tracking.createValueList();
      });
    }
  }], [stackIndex, t, tracking, state.data]);
  const onCreateFieldsTab = useCallback(entities => {
    formTab.current.open({
      entities,
      entity
    });
  }, [entity]);
  const tabIdActions = useMemo(() => [{
    label: t('cfm_action_create_tab'),
    onClick: () => {
      if (isWeb && !canCreateTabs) onOpenUpgradeTabsInfo();else {
        setStackIndex(stackIndex + 1);
        onCreateFieldsTab();
      }
    }
  }], [t, onOpenUpgradeTabsInfo, isWeb, canCreateTabs, onCreateFieldsTab, stackIndex]);
  const customValidationsActions = useMemo(() => [{
    label: t('cfm_action_create_validation'),
    onClick: () => {
      var _state$data$type;
      setStackIndex(stackIndex + 1);
      formValidations.current.open({
        type: ((_state$data$type = state.data.type) === null || _state$data$type === void 0 ? void 0 : _state$data$type.value) || null
      });
    }
  }], [stackIndex, state.data, t]);
  const customOption = useCallback(data => {
    return /*#__PURE__*/React.createElement("div", {
      className: classes === null || classes === void 0 ? void 0 : classes.typeOptions
    }, /*#__PURE__*/React.createElement(Icon, {
      className: classes === null || classes === void 0 ? void 0 : classes.icon,
      name: FIELD_TYPE[data.value]
    }), /*#__PURE__*/React.createElement("span", null, data.label));
  }, [classes]);
  const onSearchCustomValidation = useCallback(function (search) {
    var _state$data$type2;
    if (search === void 0) {
      search = '';
    }
    return getFieldsValidations({
      page: 0,
      name: search,
      fieldType: (_state$data$type2 = state.data.type) === null || _state$data$type2 === void 0 ? void 0 : _state$data$type2.value,
      count: 999999999
    }).then(_ref9 => {
      let {
        data
      } = _ref9;
      return data.map(el => ({
        value: el.id,
        label: el.name
      }));
    });
  }, [state.data.type]);
  const filterValues = useCallback(values => {
    let entities = Object.keys(entitiesLiterals);
    entities = [...entities, 'viewContactosFullName', 'viewUsuariosFullName', 'productos'];
    return values === null || values === void 0 ? void 0 : values.filter(el => !entities.includes(el.value));
  }, []);
  const onSearchValues = useCallback(function (text) {
    var _state$tables;
    if (text === void 0) {
      text = 'a';
    }
    return searchValues({
      entity: state === null || state === void 0 ? void 0 : (_state$tables = state.tables) === null || _state$tables === void 0 ? void 0 : _state$tables.dynamicFieldValues,
      text,
      lang: language
    }).then(_ref0 => {
      let {
        data
      } = _ref0;
      return data.map(item => ({
        label: item.description,
        value: item.id
      }));
    }).catch(err => {
      console.error(err);
    });
  }, [language, state.tables.dynamicFieldValues]);
  const modifyFieldProps = useCallback(field => {
    var _state$data9, _state$data9$type, _state$data0, _state$data0$valueLis, _state$data1, _state$data1$type, _state$data10, _state$data10$valueLi, _state$data11, _state$data11$type, _permissions$valueLis, _permissions$valueLis2, _state$data12, _state$data12$valueLi, _permissions$fieldsVa, _state$data13, _state$data13$type, _state$data14, _state$data14$type, _permissions$fieldsVa2, _state$data15, _state$data15$entity, _state$data16, _state$data16$entity, _permissions$extraFie, _state$data17, _state$data18, _state$data18$entity, _state$data19, _state$data20, _state$data21, _state$data22, _state$data22$entity, _state$data23, _state$data23$type, _state$data24, _state$data24$type, _state$data25, _state$data25$entity, _state$data26, _state$data26$type;
    let newField = field;
    let newsSchema = schema.current;
    const overrideIsHidden = id => Array.isArray(fieldsToHide) && fieldsToHide.includes(id);
    switch (field.schemaId || field.id) {
      case 'fieldName':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          hint: t('cfm_label_fieldname_api_name_help'),
          isReadOnly: state.crudType === CRUD_TYPE_EDIT,
          isHidden: overrideIsHidden('fieldName')
        });
        break;
      case 'entity':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          options: state.valueLists.entity,
          isReadOnly: state.crudType === CRUD_TYPE_EDIT,
          isHidden: overrideIsHidden('entity')
        });
        break;
      case 'type':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          options: state.valueLists.type,
          overrides: {
            'react-select': {
              formatOptionLabel: customOption
            }
          },
          isReadOnly: state.crudType === CRUD_TYPE_EDIT
        });
        switch ((_state$data9 = state.data) === null || _state$data9 === void 0 ? void 0 : (_state$data9$type = _state$data9.type) === null || _state$data9$type === void 0 ? void 0 : _state$data9$type.value) {
          case 'datetime':
            newsSchema.layout.composition[9].uiType = 'text';
            newsSchema.layout.composition[6].isHidden = false;
            newsSchema.layout.composition[9].isHidden = true;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[7].isHidden = true;
            break;
          case 'bit':
            newsSchema.layout.composition[9].uiType = 'checkbox';
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].isHidden = false;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[7].isHidden = true;
            break;
          case 'int':
            newsSchema.layout.composition[9].uiType = 'integer';
            newsSchema.layout.composition[9].placeholder = 'cfm_placeholder_numeric';
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].isHidden = false;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[7].isHidden = true;
            break;
          case 'multivalue':
          case 'list':
            newsSchema.layout.composition[9].placeholder = 'label_selectone';
            newsSchema.layout.composition[9].uiType = 'list';
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].valueObjectNeeded = {
              table: (_state$data0 = state.data) === null || _state$data0 === void 0 ? void 0 : (_state$data0$valueLis = _state$data0.valueListRelated) === null || _state$data0$valueLis === void 0 ? void 0 : _state$data0$valueLis.value
            };
            newsSchema.layout.composition[9].placeholder = 'label_selectone';
            newsSchema.layout.composition[9].isHidden = false;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[7].isHidden = false;
            break;
          case 'percentage':
            newsSchema.layout.composition[9].uiType = 'number';
            newsSchema.layout.composition[9].placeholder = 'cfm_placeholder_percentage';
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].isHidden = false;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[7].isHidden = true;
            break;
          case 'decimal':
            newsSchema.layout.composition[9].uiType = 'number';
            newsSchema.layout.composition[9].placeholder = 'cfm_placeholder_decimal';
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].isHidden = false;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[7].isHidden = true;
            break;
          case 'currency':
            newsSchema.layout.composition[9].uiType = 'number';
            newsSchema.layout.composition[9].placeholder = 'cfm_placeholder_percentage';
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].isHidden = false;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[7].isHidden = true;
            break;
          case 'text':
            newsSchema.layout.composition[9].uiType = 'text';
            newsSchema.layout.composition[9].placeholder = 'cfm_placeholder_type_value';
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].isHidden = false;
            newsSchema.layout.composition[10].isHidden = false;
            newsSchema.layout.composition[7].isHidden = true;
            break;
          default:
            newsSchema.layout.composition[6].isHidden = true;
            newsSchema.layout.composition[9].isHidden = true;
            newsSchema.layout.composition[10].isHidden = true;
            newsSchema.layout.composition[10].isHidden = true;
            break;
        }
        if (!((_state$data1 = state.data) !== null && _state$data1 !== void 0 && (_state$data1$type = _state$data1.type) !== null && _state$data1$type !== void 0 && _state$data1$type.value)) {
          newsSchema.layout.composition[6].isHidden = true;
          newsSchema.layout.composition[7].isHidden = true;
          newsSchema.layout.composition[9].isHidden = true;
        }
        break;
      case 'defaultValue':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          hint: t('cfm_label_default_value_help'),
          key: (_state$data10 = state.data) === null || _state$data10 === void 0 ? void 0 : (_state$data10$valueLi = _state$data10.valueListRelated) === null || _state$data10$valueLi === void 0 ? void 0 : _state$data10$valueLi.value,
          isHidden: overrideIsHidden('defaultValue')
        });
        break;
      case 'valueListRelated':
        const {
          loadedValues
        } = state;
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          options: ((_state$data11 = state.data) === null || _state$data11 === void 0 ? void 0 : (_state$data11$type = _state$data11.type) === null || _state$data11$type === void 0 ? void 0 : _state$data11$type.value) === 'multivalue' ? filterValues(state.valueLists.valueListRelated) : state.valueLists.valueListRelated,
          loadedValues
        });
        if (overridePermissions || permissions !== null && permissions !== void 0 && (_permissions$valueLis = permissions.valueList) !== null && _permissions$valueLis !== void 0 && _permissions$valueLis.create) {
          newField = _objectSpread(_objectSpread({}, newField), {}, {
            actions: valueListRelatedActions
          });
        }
        if (overridePermissions || permissions !== null && permissions !== void 0 && (_permissions$valueLis2 = permissions.valueList) !== null && _permissions$valueLis2 !== void 0 && _permissions$valueLis2.update) {
          newField = _objectSpread(_objectSpread({}, newField), {}, {
            onClickButtonList
          });
        }
        newsSchema.context = _objectSpread(_objectSpread({}, newsSchema.context), {}, {
          fields: _objectSpread(_objectSpread({}, newsSchema.context.fields), {}, {
            defaultValue: {
              table: (_state$data12 = state.data) === null || _state$data12 === void 0 ? void 0 : (_state$data12$valueLi = _state$data12.valueListRelated) === null || _state$data12$valueLi === void 0 ? void 0 : _state$data12$valueLi.value
            }
          })
        });
        break;
      case 'customValidation':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          actions: !hideCreateValidation && (permissions === null || permissions === void 0 ? void 0 : (_permissions$fieldsVa = permissions.fieldsValidations) === null || _permissions$fieldsVa === void 0 ? void 0 : _permissions$fieldsVa.create) && customValidationsActions,
          loadOptionsWithFuzzy: onSearchCustomValidation,
          isFuzzy: true,
          overrides: {
            'react-select': {
              defaultOptions: true,
              openMenuOnClick: true
            }
          },
          isHidden: overrideIsHidden('customValidation') || !((_state$data13 = state.data) !== null && _state$data13 !== void 0 && (_state$data13$type = _state$data13.type) !== null && _state$data13$type !== void 0 && _state$data13$type.value) || !allowedTypesOfValidation.includes((_state$data14 = state.data) === null || _state$data14 === void 0 ? void 0 : (_state$data14$type = _state$data14.type) === null || _state$data14$type === void 0 ? void 0 : _state$data14$type.value) || !overridePermissions && !(permissions !== null && permissions !== void 0 && (_permissions$fieldsVa2 = permissions.fieldsValidations) !== null && _permissions$fieldsVa2 !== void 0 && _permissions$fieldsVa2.rootFieldsValidations) || !allowedForCustomValidations.includes((_state$data15 = state.data) === null || _state$data15 === void 0 ? void 0 : (_state$data15$entity = _state$data15.entity) === null || _state$data15$entity === void 0 ? void 0 : _state$data15$entity.value)
        });
        break;
      case 'relevantFields':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          isHidden: overrideIsHidden('relevantFields') || !allowedForRelevantFields.includes((_state$data16 = state.data) === null || _state$data16 === void 0 ? void 0 : (_state$data16$entity = _state$data16.entity) === null || _state$data16$entity === void 0 ? void 0 : _state$data16$entity.value)
        });
        break;
      case 'tabId':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          actions: tabIdActions,
          options: state.valueLists.tabs
        });
        break;
      case 'description':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          hint: t('cfm_label_field_name_help')
        });
        break;
      case 'isEditable':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          hint: t('cfm_field_user_editable_help'),
          isHidden: !(permissions !== null && permissions !== void 0 && (_permissions$extraFie = permissions.extraFields) !== null && _permissions$extraFie !== void 0 && _permissions$extraFie.userUpdate) || overridePermissions
        });
        break;
      case 'branches':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          isHidden: overrideIsHidden('branches'),
          hint: t('cfm_label_visibility_environments_help')
        });
        break;
      case 'dynamicField':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          options: state.valueLists.dynamicField,
          isReadOnly: !((_state$data17 = state.data) !== null && _state$data17 !== void 0 && _state$data17.entity),
          hint: t('cfm_label_tooltip_dynamic_fields'),
          isHidden: overrideIsHidden('dynamicField') || forbiddenForDynamicFields.includes((_state$data18 = state.data) === null || _state$data18 === void 0 ? void 0 : (_state$data18$entity = _state$data18.entity) === null || _state$data18$entity === void 0 ? void 0 : _state$data18$entity.value)
        });
        break;
      case 'dynamicFieldValues':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          isReadOnly: !((_state$data19 = state.data) !== null && _state$data19 !== void 0 && _state$data19.entity) || !((_state$data20 = state.data) !== null && _state$data20 !== void 0 && _state$data20.dynamicField),
          isRequired: (_state$data21 = state.data) === null || _state$data21 === void 0 ? void 0 : _state$data21.dynamicField,
          table: state.tables.dynamicFieldValues,
          loadOptionsWithFuzzy: onSearchValues,
          key: state.tables.dynamicFieldValues,
          isHidden: overrideIsHidden('dynamicFieldValues') || forbiddenForDynamicFields.includes((_state$data22 = state.data) === null || _state$data22 === void 0 ? void 0 : (_state$data22$entity = _state$data22.entity) === null || _state$data22$entity === void 0 ? void 0 : _state$data22$entity.value)
        });
        break;
      case 'dependenceField':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          options: state.valueLists.dependenceField,
          hint: t('cfm_label_tooltip_dependant_fields'),
          isHidden: overrideIsHidden('dependenceField'),
          isReadOnly: ((_state$data23 = state.data) === null || _state$data23 === void 0 ? void 0 : (_state$data23$type = _state$data23.type) === null || _state$data23$type === void 0 ? void 0 : _state$data23$type.value) !== 'list' && ((_state$data24 = state.data) === null || _state$data24 === void 0 ? void 0 : (_state$data24$type = _state$data24.type) === null || _state$data24$type === void 0 ? void 0 : _state$data24$type.value) !== 'multivalue'
        });
        newsSchema.layout.composition.forEach(item => {
          if (['dependentAlert'].includes(item.schemaId)) {
            if (state.data.dependenceField) item.isHidden = false;else item.isHidden = true;
          }
        });
        break;
      case 'checkDuplicatesSection':
        newField = _objectSpread(_objectSpread({}, newField), {}, {
          isHidden: overrideIsHidden('checkDuplicatesSection') || !(ENABLED_DUPLICATE_ENTITIES.includes(state === null || state === void 0 ? void 0 : (_state$data25 = state.data) === null || _state$data25 === void 0 ? void 0 : (_state$data25$entity = _state$data25.entity) === null || _state$data25$entity === void 0 ? void 0 : _state$data25$entity.value) && ((_state$data26 = state.data) === null || _state$data26 === void 0 ? void 0 : (_state$data26$type = _state$data26.type) === null || _state$data26$type === void 0 ? void 0 : _state$data26$type.value) === 'text'),
          className: 'fs-form__section-checkDuplicates'
        });
        break;
      case 'generalSettings':
        newField = fieldsToHide.length ? _objectSpread(_objectSpread({}, newField), {}, {
          inputs: newField.inputs.map(input => overrideIsHidden(input.name) ? _objectSpread(_objectSpread({}, input), {}, {
            isHidden: true
          }) : input)
        }) : newField;
        break;
      case 'length':
        {
          newField = _objectSpread(_objectSpread({}, newField), {}, {
            isHidden: overrideIsHidden('length')
          });
          break;
        }
      case 'literalKey':
        {
          newField = _objectSpread(_objectSpread({}, newField), {}, {
            isHidden: true || overrideIsHidden('literalKey')
          });
          break;
        }
      default:
        break;
    }
    schema.current = newsSchema;
    return newField;
  }, [fieldsToHide, t, state, customOption, filterValues, overridePermissions, permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis3 = permissions.valueList) === null || _permissions$valueLis3 === void 0 ? void 0 : _permissions$valueLis3.create, permissions === null || permissions === void 0 ? void 0 : (_permissions$valueLis4 = permissions.valueList) === null || _permissions$valueLis4 === void 0 ? void 0 : _permissions$valueLis4.update, permissions === null || permissions === void 0 ? void 0 : (_permissions$fieldsVa3 = permissions.fieldsValidations) === null || _permissions$fieldsVa3 === void 0 ? void 0 : _permissions$fieldsVa3.create, permissions === null || permissions === void 0 ? void 0 : (_permissions$fieldsVa4 = permissions.fieldsValidations) === null || _permissions$fieldsVa4 === void 0 ? void 0 : _permissions$fieldsVa4.rootFieldsValidations, permissions === null || permissions === void 0 ? void 0 : (_permissions$extraFie2 = permissions.extraFields) === null || _permissions$extraFie2 === void 0 ? void 0 : _permissions$extraFie2.userUpdate, hideCreateValidation, customValidationsActions, onSearchCustomValidation, onSearchValues, valueListRelatedActions, onClickButtonList, tabIdActions]);
  const onSaveValueListForm = useCallback(data => {
    dispatch({
      type: 'changeValueList',
      data,
      isWeb,
      dumbUser
    });
  }, [isWeb, dumbUser]);
  const onSaveFieldValidationForm = useCallback(data => {
    dispatch({
      type: 'changeValidation',
      data: {
        value: data.id,
        label: data.name
      }
    });
  }, []);
  const onStackForm = useCallback(stackIndex => {
    setStackIndex(stackIndex);
  }, []);
  const onCancel = useCallback(() => {
    setActiveRowId && setActiveRowId(null);
    dispatch({
      type: 'close'
    });
  }, [setActiveRowId]);
  const onChange = useCallback((data, field) => {
    var _data$type, _data$type2, _data$type3, _state$data27, _state$data28, _state$data28$entity, _data$valueListRelate, _data$valueListRelate2, _data$dynamicField2, _data$dynamicField3;
    let finalData;
    switch (field) {
      case 'description':
        if (fieldsToHide.includes('fieldName') && (state.crudType === CRUD_TYPE_ADD || state.crudType === CRUD_TYPE_DUPLICATE)) {
          finalData = _objectSpread(_objectSpread({}, data), {}, {
            fieldName: normalizeFieldName(data.description)
          });
        } else {
          finalData = data;
        }
        dispatch({
          type: 'changeData',
          data: finalData
        });
        break;
      case 'type':
        finalData = _objectSpread(_objectSpread({}, data), {}, {
          defaultValue: null,
          valueListRelated: null,
          customValidation: null,
          dependenceField: ((_data$type = data.type) === null || _data$type === void 0 ? void 0 : _data$type.value) === 'list' || ((_data$type2 = data.type) === null || _data$type2 === void 0 ? void 0 : _data$type2.value) === 'multivalue' ? data.dependenceField : null,
          includeTime: ((_data$type3 = data.type) === null || _data$type3 === void 0 ? void 0 : _data$type3.value) === 'datetime'
        });
        dispatch({
          type: 'changeData',
          data: finalData
        });
        break;
      case 'entity':
        if (data.entity) getTabs(data.entity.value).then(_ref1 => {
          let {
            data
          } = _ref1;
          dispatch({
            type: 'changeTabs',
            tabs: getTabsOptions(data, t, customGeneralTab)
          });
        });
        const valueLists = {
          dynamicField: formatDynamicFields((_state$data27 = state.data) === null || _state$data27 === void 0 ? void 0 : _state$data27.fieldName, (_state$data28 = state.data) === null || _state$data28 === void 0 ? void 0 : (_state$data28$entity = _state$data28.entity) === null || _state$data28$entity === void 0 ? void 0 : _state$data28$entity.value, state.fields, language, t)
        };
        dispatch({
          type: 'changeEntity',
          data,
          valueLists
        });
        break;
      case 'length':
        finalData = _objectSpread({}, data);
        if (data.length > 4000) finalData.length = 4000;
        dispatch({
          type: 'changeData',
          data: finalData
        });
        break;
      case 'valueListRelated':
        schema.current.context = _objectSpread(_objectSpread({}, schema.current.context), {}, {
          fields: _objectSpread(_objectSpread({}, schema.current.context.fields), {}, {
            defaultValue: {
              table: (data === null || data === void 0 ? void 0 : (_data$valueListRelate = data.valueListRelated) === null || _data$valueListRelate === void 0 ? void 0 : _data$valueListRelate.label) || null
            }
          })
        });
        finalData = _objectSpread(_objectSpread({}, data), {}, {
          fuzzySearch: isEntityList(data === null || data === void 0 ? void 0 : (_data$valueListRelate2 = data.valueListRelated) === null || _data$valueListRelate2 === void 0 ? void 0 : _data$valueListRelate2.label),
          defaultValue: null
        });
        dispatch({
          type: 'changeData',
          data: finalData
        });
        break;
      case 'dynamicField':
        const tablesDynamicFieldValues = data === null || data === void 0 ? void 0 : (_data$dynamicField2 = data.dynamicField) === null || _data$dynamicField2 === void 0 ? void 0 : _data$dynamicField2.valueListRelated;
        schema.current.context = _objectSpread(_objectSpread({}, schema.current.context), {}, {
          fields: _objectSpread(_objectSpread({}, schema.current.context.fields), {}, {
            dynamicFieldValues: {
              table: data === null || data === void 0 ? void 0 : (_data$dynamicField3 = data.dynamicField) === null || _data$dynamicField3 === void 0 ? void 0 : _data$dynamicField3.valueListRelated
            }
          })
        });
        dispatch({
          type: 'changeDataAndTables',
          data,
          tablesDynamicFieldValues
        });
        break;
      case 'dynamicFieldValues':
        finalData = _objectSpread(_objectSpread({}, data), {}, {
          dynamicFieldValues: data !== null && data !== void 0 && data.dynamicFieldValues.length ? data === null || data === void 0 ? void 0 : data.dynamicFieldValues : undefined
        });
        dispatch({
          type: 'changeData',
          data: finalData
        });
        break;
      case 'generalSettings':
        const layoutComposition = schema.current.layout.composition.filter(item => item.schemaId !== 'generalSettings');
        const generalSettings = schema.current.layout.composition.find(item => item.schemaId === 'generalSettings');
        const generalSettingsComposition = [...generalSettings.composition];
        const mandatoryIndex = generalSettingsComposition.findIndex(item => item.id === 'mandatory');
        const readOnlyIndex = generalSettingsComposition.findIndex(item => item.id === 'readOnly');
        const visibleInPhoneCallFormIndex = _objectSpread({}, generalSettingsComposition.find(item => item.id === 'visibleInPhoneCallForm'));
        const mandatoryRestApiIndex = generalSettingsComposition.find(item => item.id === 'mandatoryRestApi');
        generalSettingsComposition[mandatoryIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[mandatoryIndex]), {}, {
          isDisabled: data && !data.visible && !data.mobileVisibility
        });
        generalSettingsComposition[readOnlyIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[readOnlyIndex]), {}, {
          isDisabled: data && !data.visible && !data.mobileVisibility
        });
        generalSettingsComposition[visibleInPhoneCallFormIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[visibleInPhoneCallFormIndex]), {}, {
          isDisabled: data && !data.visible && !data.mobileVisibility
        });
        generalSettingsComposition[mandatoryRestApiIndex] = _objectSpread(_objectSpread({}, generalSettingsComposition[mandatoryRestApiIndex]), {}, {
          isHidden: !(data !== null && data !== void 0 && data.restApi)
        });
        const newSchema = _objectSpread(_objectSpread({}, schema.current), {}, {
          layout: _objectSpread(_objectSpread({}, schema.current.layout), {}, {
            composition: [...layoutComposition, _objectSpread(_objectSpread({}, generalSettings), {}, {
              composition: generalSettingsComposition
            })]
          })
        });
        schema.current = newSchema;
        if (!data.visible && !data.mobileVisibility) {
          data.mandatory = false;
          data.readOnly = false;
          data.visibleInPhoneCallForm = false;
        }
        dispatch({
          type: 'changeData',
          data
        });
        break;
      case 'relevantFields':
        if (entityLogSubmodule === 'accountsTab') logEvent({
          event: entityLogName,
          submodule: entityLogSubmodule,
          functionality: 'featuredField'
        });
        dispatch({
          type: 'changeData',
          data
        });
        break;
      case 'customValidation':
        dispatch({
          type: 'changeData',
          data
        });
        break;
      default:
        dispatch({
          type: 'changeData',
          data
        });
        break;
    }
  }, [fieldsToHide, state === null || state === void 0 ? void 0 : state.crudType, (_state$data29 = state.data) === null || _state$data29 === void 0 ? void 0 : _state$data29.fieldName, (_state$data30 = state.data) === null || _state$data30 === void 0 ? void 0 : (_state$data30$entity = _state$data30.entity) === null || _state$data30$entity === void 0 ? void 0 : _state$data30$entity.value, state === null || state === void 0 ? void 0 : state.fields, language, t, entityLogSubmodule, logEvent, entityLogName, customGeneralTab]);
  const onBlur = useCallback((data, field) => {
    if (field === 'description' && ((state === null || state === void 0 ? void 0 : state.crudType) === CRUD_TYPE_ADD || (state === null || state === void 0 ? void 0 : state.crudType) === CRUD_TYPE_DUPLICATE)) {
      dispatch({
        type: 'changeData',
        data: {
          fieldName: normalizeFieldName(data.description)
        }
      });
    }
    if (field === 'fieldName') {
      dispatch({
        type: 'changeData',
        data: {
          fieldName: normalizeFieldName(data.fieldName)
        }
      });
    }
  }, [state === null || state === void 0 ? void 0 : state.crudType]);
  const steps = [{
    selector: '.fs-form__section-checkDuplicates',
    title: t('cfm_label_promotion_duplicates'),
    description: t('cfm_label_promotion_duplicates_body')
  }];
  const onCloseTour = useCallback(() => {
    if (customOnCloseTour) {
      customOnCloseTour('duplicateFields');
    } else {
      const deviceCacheDuplicateFields = (deviceCache === null || deviceCache === void 0 ? void 0 : deviceCache.duplicateFields) || {};
      setDeviceCache(_objectSpread(_objectSpread({}, deviceCache), {}, {
        duplicateFields: _objectSpread(_objectSpread({}, deviceCacheDuplicateFields), {}, {
          duplicateButton: true
        })
      }));
    }
  }, [customOnCloseTour, deviceCache, setDeviceCache]);
  const shouldShowTour = useMemo(() => {
    var _state$data31, _state$data31$entity, _state$data32, _state$data32$type;
    if (avoidTours) return false;
    // Duplicates is enabled
    if (ENABLED_DUPLICATE_ENTITIES.includes(state === null || state === void 0 ? void 0 : (_state$data31 = state.data) === null || _state$data31 === void 0 ? void 0 : (_state$data31$entity = _state$data31.entity) === null || _state$data31$entity === void 0 ? void 0 : _state$data31$entity.value) && (state === null || state === void 0 ? void 0 : (_state$data32 = state.data) === null || _state$data32 === void 0 ? void 0 : (_state$data32$type = _state$data32.type) === null || _state$data32$type === void 0 ? void 0 : _state$data32$type.value) === 'text') {
      if (overrideTours && overrideTours.hasOwnProperty('duplicateFields')) return !overrideTours.duplicateFields;
      const deviceCacheDuplicateFields = (deviceCache === null || deviceCache === void 0 ? void 0 : deviceCache.duplicateFields) || {};
      if (deviceCacheDuplicateFields !== null && deviceCacheDuplicateFields !== void 0 && deviceCacheDuplicateFields.duplicateButton) return false;
      return true;
    }
    return false;
  }, [deviceCache === null || deviceCache === void 0 ? void 0 : deviceCache.duplicateFields, overrideTours, state === null || state === void 0 ? void 0 : (_state$data33 = state.data) === null || _state$data33 === void 0 ? void 0 : (_state$data33$entity = _state$data33.entity) === null || _state$data33$entity === void 0 ? void 0 : _state$data33$entity.value, state === null || state === void 0 ? void 0 : (_state$data34 = state.data) === null || _state$data34 === void 0 ? void 0 : (_state$data34$type = _state$data34.type) === null || _state$data34$type === void 0 ? void 0 : _state$data34$type.value, avoidTours]);
  useLayoutEffect(() => {
    if (shouldShowTour) {
      setTimeout(() => {
        var _document$querySelect;
        (_document$querySelect = document.querySelector(`.fs-form__section-checkDuplicates`)) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
      }, 500);
    }
  }, [shouldShowTour]);
  const defaultValues = useMemo(() => {
    var _Object$keys, _state$data35, _state$data36;
    if (!(state !== null && state !== void 0 && state.data) || !((_Object$keys = Object.keys(state === null || state === void 0 ? void 0 : state.data)) !== null && _Object$keys !== void 0 && _Object$keys.length) || !(state !== null && state !== void 0 && (_state$data35 = state.data) !== null && _state$data35 !== void 0 && _state$data35.fieldName) && !(state !== null && state !== void 0 && (_state$data36 = state.data) !== null && _state$data36 !== void 0 && _state$data36.description)) return {};
    return {
      description: state.data.fieldName || state.data.description,
      descriptionBR: '',
      descriptionDE: '',
      descriptionDK: '',
      descriptionEN: '',
      descriptionFR: '',
      descriptionIT: '',
      descriptionPT: '',
      descriptionRU: '',
      descriptionUS: '',
      name: normalizeFieldName(state.data.fieldName || state.data.description),
      apiNameV4: null
    };
  }, [state === null || state === void 0 ? void 0 : state.data]);
  const onAfterSaveTabId = useCallback(value => {
    var _state$valueLists;
    onChange(_objectSpread(_objectSpread({}, state.data || {}), {}, {
      tabId: value
    }), 'tabId');
    dispatch({
      type: 'changeTabs',
      tabs: [...((state === null || state === void 0 ? void 0 : (_state$valueLists = state.valueLists) === null || _state$valueLists === void 0 ? void 0 : _state$valueLists.tabs) || {}), value]
    });
  }, [state === null || state === void 0 ? void 0 : state.data, onChange, state === null || state === void 0 ? void 0 : state.valueLists]);
  const onClickAdvanced = useCallback(() => {
    if (onClickGoToAdmin) return onClickGoToAdmin();
    return onOpenUpgradeAdminInfo();
  }, [onClickGoToAdmin, onOpenUpgradeAdminInfo]);
  const finalOverrides = useMemo(() => _objectSpread(_objectSpread({}, overrides || {}), {}, {
    fieldsToHide: (overrides === null || overrides === void 0 ? void 0 : overrides.valueListFieldsToHide) || [],
    columnsToHide: (overrides === null || overrides === void 0 ? void 0 : overrides.valueListColumnsToHide) || [],
    fieldsToAvoidExpand: (overrides === null || overrides === void 0 ? void 0 : overrides.valueListFieldsToAvoidExpand) || []
  }), [overrides]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(HyperDrawer, {
    isOpen: state.isOpen,
    locales: locales,
    isLoading: state.isLoading,
    isSaving: state.isSaving,
    onCancel: onCancel,
    onSave: onSave,
    onSaveCreate: !hideSaveAndCreateAnother && state.crudType === CRUD_TYPE_ADD && onSaveCreate,
    stackIndex: stackIndex,
    data: state.data,
    originalData: state.originalData,
    className: "fields__extra-field-form"
  }, shouldShowTour && /*#__PURE__*/React.createElement(Tour, {
    steps: steps,
    onClose: onCloseTour,
    delay: 1000
  }), /*#__PURE__*/React.createElement(ForceForm, {
    values: state.data,
    errors: state.errors,
    onChange: onChange,
    onBlur: onBlur,
    schema: schema.current,
    mode: "create",
    className: classes.root,
    modifyFieldProps: modifyFieldProps,
    stackIndex: stackIndex,
    loadedValues: state === null || state === void 0 ? void 0 : state.loadedValues
  }), isWeb && /*#__PURE__*/React.createElement(Button, {
    type: "secondary",
    icon: "settings",
    isFullWidth: true,
    onClick: onClickAdvanced
  }, t('action_advanced_options'))), /*#__PURE__*/React.createElement(FormValueList, {
    onRef: ref => formValueLists.current = ref,
    onSave: onSaveValueListForm,
    onStack: onStackForm,
    overrides: finalOverrides,
    initialData: defaultValues
  }), /*#__PURE__*/React.createElement(FormFieldsValidations, {
    onRef: ref => formValidations.current = ref,
    onSave: onSaveFieldValidationForm,
    stackIndex: stackIndex,
    onStack: onStackForm
  }), /*#__PURE__*/React.createElement(FormTab, {
    onRef: ref => formTab.current = ref,
    refreshList: refreshList,
    fieldsData: state.fieldsData,
    getEntityData: getEntityData,
    idSetupRole: idSetupRole,
    setActiveRowId: setActiveRowId,
    overrides: _objectSpread(_objectSpread({}, overrides), {}, {
      fieldsToHide: overrides !== null && overrides !== void 0 && overrides.isWeb && overrides !== null && overrides !== void 0 && (_overrides$formTabFie = overrides.formTabFieldsToHide) !== null && _overrides$formTabFie !== void 0 && _overrides$formTabFie.length ? overrides.formTabFieldsToHide : []
    }),
    onStack: onStackForm,
    afterOnSave: onAfterSaveTabId
  }));
};
process.env.NODE_ENV !== "production" ? FormExtraField.propTypes = {
  onRef: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
  allFields: PropTypes.object,
  getEntityData: PropTypes.func.isRequired
} : void 0;
export default /*#__PURE__*/memo(FormExtraField);